import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { Row, Col, Card, Badge, Button, Alert } from "react-bootstrap";
import {
  IconShoppingCart,
  IconCheck,
  IconStar,
  IconCrown,
  IconTrendingUp,
  IconShieldCheck,
} from "@tabler/icons-react";

/**
 * ЛОГИКА:
 * 1) POST /api/orders/create-txn -> получаем txn_* (бек уже добавляет customer.email).
 * 2) Кэшируем txn в session/localStorage + ref.
 * 3) НЕ создаём черновик на фронте.
 * 4) Открываем Paddle по transactionId, передаём customer.email и в open (доп.страховка префилла).
 * 5) successUrl содержит /purchase-success?order=txn_*; редиректы делаем в parent (window.top).
 */

// --- mapping и карточки (ваши) ---
const PRICE_MAP = {
  "starter-package":   "pri_01k6mk4a6zzep1ztrydxeerrnn",
  "premium-package":   "pri_01k6mk7fwardqxz6rwswhwmdv1",
  "agency-package":    "pri_01k6mtebqfyh29hxcba6p4n0j5",
  "guru-package":      "pri_01k6mtfy5hxnxv02n5d07am1bb",
  "platinum-package":  "pri_01k6mtgnvyp10ny69gh28fj8wg",
  "titan-package":     "pri_01k6mther8gm4mdjdc2bk99bws",
};

const creditPackages = [
  { name: "Starter",   price: "$10 *",    credits: "20 credits",     pricePerCredit: "≈ $0.50 / credit*",  productId: "starter-package",   tag: "New",      color: "secondary" },
  { name: "Premium",   price: "$110 *",   credits: "250 credits",    pricePerCredit: "≈ $0.44 / credit*",  productId: "premium-package",   tag: "Popular",  color: "primary"   },
  { name: "Agency",    price: "$200 *",   credits: "500 credits",    pricePerCredit: "≈ $0.40 / credit*",  productId: "agency-package",    tag: "Deal",     color: "success"   },
  { name: "Guru",      price: "$630 *",   credits: "1,800 credits",  pricePerCredit: "≈ $0.35 / credit*",  productId: "guru-package",      tag: "Pro",      color: "indigo"    },
  { name: "Platinum",  price: "$1,200 *", credits: "4,000 credits",  pricePerCredit: "≈ $0.30 / credit*",  productId: "platinum-package",  tag: "Scale",    color: "orange"    },
  { name: "Titan",     price: "$2,600 *", credits: "10,400 credits", pricePerCredit: "≈ $0.25 / credit*",  productId: "titan-package",     tag: "Best ROI", color: "teal"      },
];

const features = [
  { text: "1 credit = 1 indexed URL", icon: <IconShieldCheck size={16} className="me-2" /> },
  { text: "Integrated automatic indexing check", icon: <IconShieldCheck size={16} className="me-2" /> },
  { text: "Re-credited within 10 days if not indexed", icon: <IconShieldCheck size={16} className="me-2" /> },
  { text: "Detailed history and results", icon: <IconShieldCheck size={16} className="me-2" /> },
  { text: "No monthly subscription", icon: <IconShieldCheck size={16} className="me-2" /> },
  { text: "Credits never expire", icon: <IconShieldCheck size={16} className="me-2" /> },
  { text: "Free API access", icon: <IconShieldCheck size={16} className="me-2" /> },
];

export default function BuyCredits() {
  const [loading, setLoading] = useState(false);
  const [paddleReady, setPaddleReady] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [me, setMe] = useState(null); // мягко получаем профиль для email → Checkout.open
  const txnRef = useRef("");

  useEffect(() => {
    const P = window.Paddle;
    setPaddleReady(!!(P && P.Checkout && P.Checkout.open));
    if (!P?.Events?.on) return;

    const onCompleted = () => {
      const tx =
        txnRef.current ||
        sessionStorage.getItem("last_txn") ||
        localStorage.getItem("last_txn") ||
        "";
      if (!tx) {
        setTimeout(() => {
          const late =
            txnRef.current ||
            sessionStorage.getItem("last_txn") ||
            localStorage.getItem("last_txn") ||
            "";
          if (late) (window.top || window).location.href = `/purchase-success?order=${encodeURIComponent(late)}`;
        }, 800);
        return;
      }
      (window.top || window).location.href = `/purchase-success?order=${encodeURIComponent(tx)}`;
    };
    const onClosed = () => {
      const tx =
        txnRef.current ||
        sessionStorage.getItem("last_txn") ||
        localStorage.getItem("last_txn") ||
        "";
      if (tx) (window.top || window).location.href = `/purchase-success?order=${encodeURIComponent(tx)}`;
    };

    P.Events.on("checkout.completed", onCompleted);
    P.Events.on("checkout.closed", onClosed);

    return () => {
      try { P.Events.off("checkout.completed", onCompleted); } catch {}
      try { P.Events.off("checkout.closed", onClosed); } catch {}
    };
  }, []);

  // мягкий профиль — если /api/me есть, используем email; если 404 — просто пропустим
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetchWithAuth("/api/auth/profile");
        if (r.ok) {
          const d = await r.json();
          if (mounted) setMe(d);
        }
      } catch {}
    })();
    return () => { mounted = false; };
  }, []);

  const saveTxnLocal = (tx) => {
    txnRef.current = tx;
    try {
      sessionStorage.setItem("last_txn", tx);
      localStorage.setItem("last_txn", tx);
    } catch {}
  };

  const openCheckout = async (productId) => {
    const P = window.Paddle;
    if (!P?.Checkout?.open) {
      setError("Paddle не инициализирован. Проверь подключение скрипта.");
      return;
    }
    const price_id = PRICE_MAP[productId];
    if (!price_id) {
      setError("Не найден price_id для выбранного пакета.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      // 1) создать транзакцию → txn_* (бек тоже проставит customer.email)
      let resp = await fetchWithAuth("/api/orders/create-txn", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price_id, quantity: 1 }),
      });
      if (resp.status === 405) {
        resp = await fetchWithAuth(`/api/orders/create-txn?price_id=${encodeURIComponent(price_id)}&quantity=1`);
      }
      if (!resp.ok) {
        const t = await resp.text().catch(() => "");
        setError(t || "Не удалось создать транзакцию.");
        return;
      }
      const data = await resp.json();
      const tx = data?.reference;
      if (!tx) {
        setError("Paddle не вернул ID транзакции.");
        return;
      }

      // 2) сохранить txn
      saveTxnLocal(tx);

      // 3) открыть overlay по transactionId; в open тоже передаём email (на всякий случай)
      const email = me?.email ? String(me.email).trim() : undefined;
      P.Checkout.open({
        transactionId: tx,
        customer: email ? { email } : undefined,
        settings: {
          displayMode: "overlay",
          theme: "light",
          successUrl: `${window.location.origin}/purchase-success?order=${encodeURIComponent(tx)}`,
        },
        success: () => {
          setSuccess({ order: tx });
          (window.top || window).location.href = `/purchase-success?order=${encodeURIComponent(tx)}`;
        },
        close: () => {
          (window.top || window).location.href = `/purchase-success?order=${encodeURIComponent(tx)}`;
        },
      });
    } catch {
      setError("Не удалось открыть окно оплаты.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Buy Credits - IndexBooster</title></Helmet>

      <div className="container-xl mt-4">
        <div className="d-flex align-items-center mb-3">
          <div className="me-2">
            <span className="avatar bg-primary-lt">
              <IconShoppingCart size={20} className="text-primary" />
            </span>
          </div>
          <div>
            <h2 className="m-0">Buy Credits</h2>
            <div className="text-secondary">Choose a one-time package — your credits never expire.</div>
          </div>
        </div>

        {success && (
          <Alert variant="success" className="d-flex align-items-center">
            <IconCheck size={18} className="me-2" />
            Payment successful. Credits added to your balance.
            <a className="ms-2" href={`/purchase-success?order=${success?.order}`}>View details</a>
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="d-flex align-items-center">{error}</Alert>
        )}

        <Row className="g-3 g-md-4">
          {creditPackages.map((pkg, index) => {
            const isHighlight = ["Premium", "Titan"].includes(pkg.name);
            const cardClass = `h-100 ${isHighlight ? "border-2 border-primary" : "border-0 shadow-sm"}`;
            return (
              <Col key={index} md={6} lg={4}>
                <Card className={cardClass}>
                  {isHighlight && (
                    <div className="card-stamp">
                      <div className="card-stamp-icon bg-primary">
                        {pkg.name === "Titan" ? <IconCrown size={20} /> : <IconStar size={20} />}
                      </div>
                    </div>
                  )}

                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="h4 m-0">{pkg.name}</div>
                      <Badge bg={`${pkg.color}-lt`} text={pkg.color} className="text-uppercase">{pkg.tag}</Badge>
                    </div>

                    <div className="mb-2">
                      <div className="text-secondary">Credits</div>
                      <div className="h2 m-0">{pkg.credits}</div>
                    </div>

                    <div className="mb-2">
                      <div className="text-secondary">Price</div>
                      <div className="d-flex align-items-baseline gap-2">
                        <div className="display-6 m-0 lh-1">{pkg.price.replace(" *", "")}</div>
                        <span className="text-muted small">one-time</span>
                      </div>
                      <div className="mt-1">
                        <Badge bg="secondary-lt" text="secondary">{pkg.pricePerCredit}</Badge>
                      </div>
                    </div>

                    <div className="mt-3">
                      <ul className="list-unstyled small mb-0">
                        {features.map((f, i) => (
                          <li key={i} className="d-flex align-items-start mb-1">
                            {f.icon}
                            <span>{f.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-auto pt-3">
                      <Button
                        variant={isHighlight ? "primary" : "outline-primary"}
                        className="w-100"
                        disabled={!paddleReady || loading}
                        onClick={() => openCheckout(pkg.productId)}
                        title={paddleReady ? "Proceed to checkout" : "Initializing checkout..."}
                      >
                        <IconShoppingCart size={18} className="me-2" />
                        {paddleReady ? (loading ? "Opening Checkout…" : "Buy Now") : "Loading Checkout…"}
                      </Button>
                    </div>
                  </Card.Body>

                  <div className="card-footer border-0 bg-transparent">
                    <div className="d-flex align-items-center text-secondary small">
                      <IconTrendingUp size={16} className="me-1" />
                      <span>Best price per credit in higher tiers</span>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    </>
  );
}
